import { useState, useEffect, useCallback } from 'react';

/**
 * Спільний стан шапки сторінки «Фінансовий результат».
 *
 * Живе тут, бо його поділяють ДВА місця: тіло сторінки і винесене в топбар
 * керування (період · дата/місяць/діапазон · «Факт. розрахунок» · «Закрити
 * місяць»). Той самий прийом, що й useForecastMmsDate/useSelectedAsset —
 * модульне значення + подія синхронізації в межах вкладки. Станцію не тримаємо
 * тут: її дає спільний useSelectedAsset.
 *
 * Без localStorage: період/дата скидаються на типові при перезавантаженні.
 */
const EV = 'helios:finresult-ui-change';

export type FinPeriod = 'day' | 'month' | 'custom';

const todayISO = (): string => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

/** Зсув ISO-дати на ±N діб (UTC, без стрибків через літній/зимовий час). */
export function shiftDay(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/** Зсув 'YYYY-MM' на ±N місяців (UTC). */
export function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
}

const state = {
  period: 'day' as FinPeriod,
  date: todayISO(),
  month: todayISO().slice(0, 7),
  from: todayISO().slice(0, 8) + '01',   // власний період: початок = 1-ше поточного місяця
  to: todayISO(),                         // ... кінець = сьогодні
  wCode: null as string | null,
  entryNonce: 0,  // інкремент = запит відкрити drawer «Факт. розрахунок»
  closeNonce: 0,  // інкремент = запит перемкнути закриття місяця
};

const emit = () => window.dispatchEvent(new CustomEvent(EV));

export function useFinancialResultUi() {
  const [, force] = useState(0);
  useEffect(() => {
    const refresh = () => force((x) => x + 1);
    window.addEventListener(EV, refresh);
    return () => window.removeEventListener(EV, refresh);
  }, []);

  const setPeriod = useCallback((p: FinPeriod) => { state.period = p; emit(); }, []);
  const setDate = useCallback((d: string) => { state.date = d; emit(); }, []);
  const setMonth = useCallback((m: string) => { state.month = m; emit(); }, []);
  const setFrom = useCallback((d: string) => { state.from = d; if (state.to < d) state.to = d; emit(); }, []);
  const setTo = useCallback((d: string) => { state.to = d; if (state.from > d) state.from = d; emit(); }, []);
  const setWCode = useCallback((w: string | null) => { if (state.wCode !== w) { state.wCode = w; emit(); } }, []);
  const requestEntry = useCallback(() => { state.entryNonce += 1; emit(); }, []);
  const requestClose = useCallback(() => { state.closeNonce += 1; emit(); }, []);

  return {
    period: state.period, date: state.date, month: state.month, from: state.from, to: state.to,
    wCode: state.wCode, entryNonce: state.entryNonce, closeNonce: state.closeNonce,
    setPeriod, setDate, setMonth, setFrom, setTo, setWCode, requestEntry, requestClose,
  };
}
